import React, { useState } from 'react';
import { Card, Table, Form, Badge } from 'react-bootstrap';

const WasteResourcesList = ({ wasteData, accountFilter }) => {
  const [searchQuery, setSearchQuery] = useState('');

  if (!wasteData || wasteData.summary?.totalWasteResources === 0) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5">
          <h5 className="text-muted">No Waste Resources Found</h5>
        </Card.Body>
      </Card>
    );
  }

  const idleInstances = wasteData.idleInstances?.items || [];
  const stoppedInstances = wasteData.stoppedInstances?.items || [];
  const unattachedVolumes = wasteData.unattachedVolumes?.items || [];
  const unusedEIPs = wasteData.unusedEIPs?.items || [];
  const oldSnapshots = wasteData.oldSnapshots?.items || [];

  const filterBySearch = (items, searchFields) => {
    if (!searchQuery) return items;
    return items.filter(item => 
      searchFields.some(field => 
        item[field]?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Waste Resource Details</h5>
          <Badge bg="warning" text="dark" style={{ fontSize: '14px' }}>
            {parseInt(wasteData.summary?.totalWasteResources || 0)} Total
          </Badge>
        </div>

        <Form.Control
          type="text"
          placeholder="Search resources..."
          className="mb-3"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="row g-3">
          {parseInt(wasteData.idleInstances?.count || 0) > 0 && (
            <div className="col-md-12">
              <h6 className="mb-2">
                <Badge bg="danger" className="me-2">{parseInt(wasteData.idleInstances?.count || 0)}</Badge>
                Idle EC2 Instances
              </h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Instance ID</th>
                      <th>Type</th>
                      <th>Region</th>
                      <th>Avg CPU</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterBySearch(idleInstances, ['id', 'name', 'region']).map((inst, idx) => (
                      <tr key={idx}>
                        <td><code>{inst.id}</code></td>
                        <td><Badge bg="secondary">{inst.type}</Badge></td>
                        <td>{inst.region}</td>
                        <td><Badge bg="danger">{inst.avgCPU}%</Badge></td>
                        <td>{inst.name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {parseInt(wasteData.stoppedInstances?.count || 0) > 0 && (
            <div className="col-md-12">
              <h6 className="mb-2">
                <Badge bg="warning" className="me-2">{parseInt(wasteData.stoppedInstances?.count || 0)}</Badge>
                Stopped EC2 Instances
              </h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Instance ID</th>
                      <th>Type</th>
                      <th>Region</th>
                      <th>Stopped Days</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterBySearch(stoppedInstances, ['id', 'name', 'region']).map((inst, idx) => (
                      <tr key={idx}>
                        <td><code>{inst.id}</code></td>
                        <td><Badge bg="secondary">{inst.type}</Badge></td>
                        <td>{inst.region}</td>
                        <td><Badge bg="warning">{inst.stoppedDays} days</Badge></td>
                        <td>{inst.name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {parseInt(wasteData.unattachedVolumes?.count || 0) > 0 && (
            <div className="col-md-12">
              <h6 className="mb-2">
                <Badge bg="info" className="me-2">{parseInt(wasteData.unattachedVolumes?.count || 0)}</Badge>
                Unattached EBS Volumes
              </h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Volume ID</th>
                      <th>Size</th>
                      <th>Type</th>
                      <th>Region</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterBySearch(unattachedVolumes, ['id', 'region']).map((vol, idx) => (
                      <tr key={idx}>
                        <td><code>{vol.id}</code></td>
                        <td><Badge bg="info">{vol.size} GB</Badge></td>
                        <td>{vol.type}</td>
                        <td>{vol.region}</td>
                        <td>{new Date(vol.createTime).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {parseInt(wasteData.unusedEIPs?.count || 0) > 0 && (
            <div className="col-md-12">
              <h6 className="mb-2">
                <Badge bg="secondary" className="me-2">{parseInt(wasteData.unusedEIPs?.count || 0)}</Badge>
                Unused Elastic IPs
              </h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>IP Address</th>
                      <th>Allocation ID</th>
                      <th>Region</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterBySearch(unusedEIPs, ['ip', 'allocationId', 'region']).map((eip, idx) => (
                      <tr key={idx}>
                        <td><code>{eip.ip}</code></td>
                        <td><code>{eip.allocationId}</code></td>
                        <td>{eip.region}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {parseInt(wasteData.oldSnapshots?.count || 0) > 0 && (
            <div className="col-md-12">
              <h6 className="mb-2">
                <Badge bg="secondary" className="me-2">{parseInt(wasteData.oldSnapshots?.count || 0)}</Badge>
                Old EBS Snapshots
              </h6>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <Table hover responsive size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Snapshot ID</th>
                      <th>Size</th>
                      <th>Age</th>
                      <th>Region</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterBySearch(oldSnapshots, ['id', 'region']).map((snap, idx) => (
                      <tr key={idx}>
                        <td><code>{snap.id}</code></td>
                        <td><Badge bg="secondary">{snap.size} GB</Badge></td>
                        <td><Badge bg="warning">{snap.age} days</Badge></td>
                        <td>{snap.region}</td>
                        <td>{new Date(snap.startTime).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {accountFilter === null && (
          <div className="alert alert-info mt-3 mb-0">
            <small>
              <strong>Note:</strong> Detailed resource list is only available when viewing a specific account. 
              Select an account from the dropdown above to see individual resources.
            </small>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default WasteResourcesList;
